import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import TopBar from '../components/TopBar';
import PageTransition from '../components/ui/PageTransition';
import Button from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import { AnimatePresence } from 'framer-motion';
import './Login.css';

const Login: React.FC = () => {
  const [cpf, setCpf] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const navigate = useNavigate();
  const { setAuthData } = useAuth();

  const formatCPF = (value: string): string => {
    let cpf = value.replace(/\D/g, '');
    if (cpf.length > 11) cpf = cpf.slice(0, 11);
    if (cpf.length <= 3) return cpf;
    if (cpf.length <= 6) return `${cpf.slice(0, 3)}.${cpf.slice(3)}`;
    if (cpf.length <= 9) return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6)}`;
    return `${cpf.slice(0, 3)}.${cpf.slice(3, 6)}.${cpf.slice(6, 9)}-${cpf.slice(9)}`;
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);

    const cleanCpf = cpf.replace(/\D/g, '');
    if (cleanCpf.length !== 11) {
      setError('CPF deve conter 11 dígitos.');
      setIsLoading(false);
      setAttempt((a) => a + 1);
      return;
    }
    if (!password) {
      setError('Senha é obrigatória.');
      setIsLoading(false);
      setAttempt((a) => a + 1);
      return;
    }

    try {
      const response = await api.post('/auth/login', {
        cpf: cleanCpf,
        password,
      });
      const { token, user } = response.data;
      setAuthData(user.id.toString(), token, user.email);
      navigate('/rj/census-choice');
    } catch (error: any) {
      setError(error.response?.data?.message || 'Erro ao fazer login. Verifique suas credenciais.');
      setAttempt((a) => a + 1);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <TopBar />
      <PageTransition>
        <div className="login-wrapper">
          <div className="login-content">
            <div className="login-image-section">
              <img src="/image.jpg" alt="Imagem de Login" width={888} height={666} />
            </div>
            <div className="login-form-section">
              <form className={`login-form${error ? ' shake' : ''}`} onSubmit={handleLogin} key={attempt}>
                <h2>Login</h2>
                <AnimatePresence>
                  {error && <Alert variant="error">{error}</Alert>}
                </AnimatePresence>
                <input
                  value={cpf}
                  onChange={(e) => setCpf(formatCPF(e.target.value))}
                  placeholder="CPF"
                  className="login-input"
                  maxLength={14}
                  disabled={isLoading}
                  aria-label="CPF"
                />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Senha"
                  className="login-input"
                  disabled={isLoading}
                  aria-label="Senha"
                />
                <Button type="submit" loading={isLoading} disabled={isLoading} className="login-button">
                  {isLoading ? 'Entrando…' : 'Entrar'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </PageTransition>
    </>
  );
};

export default Login;